import { type ChatDto, type MessageDto } from '@/core/data/chat/chat.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import { collection, doc, onSnapshot, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { ref } from 'vue';

// interface ChatDocument {
//   id: string;
//   updatedAt: Timestamp;
//   // currentSubscribers?: number;
// }

interface MessageDocument {
  id: string;
  body: string;
  chatId: string;
  username: string;
  createdAt: Timestamp;
}

type UnsubscribeCallback = () => void;

const logger = createLogger('ChatStore');

export const useChatStore = defineStore('gambitChat', () => {
  const chat = ref<ChatDto | null>(null);
  const isChatLoading = ref<boolean>(false);
  const isChatError = ref<boolean>(false);
  const isChatNotFound = ref<boolean>(false);

  const areMessagesInitialized = ref<boolean>(false);
  const areMessagesLoading = ref<boolean>(false);
  const areMessagesError = ref<boolean>(false);
  const areMessagesNotFound = ref<boolean>(false);
  const messages = ref<MessageDto[]>([]);

  const isMessageSubmitting = ref<boolean>(false);
  const isMessageError = ref<boolean>(false);
  const isMessageDeleting = ref<boolean>(false);
  const isMessageDeleteError = ref<boolean>(false);

  let unsubscribeChat: UnsubscribeCallback | null = null;
  let unsubscribeMessages: UnsubscribeCallback | null = null;
  let currentChatId: string | null = null;
  let didExist = false;

  const clear = async () => {
    currentChatId = null;

    if (unsubscribeChat !== null) {
      await unsubscribeChat();
      unsubscribeChat = null;
    }
    if (unsubscribeMessages !== null) {
      await unsubscribeMessages();
      unsubscribeMessages = null;
    }

    isChatLoading.value = false;
    areMessagesLoading.value = false;
    isChatError.value = false;
    areMessagesError.value = false;
    isChatNotFound.value = false;
    areMessagesNotFound.value = false;
    chat.value = null;
    areMessagesInitialized.value = false;
    messages.value = [];
  };

  const subscribeToChat = (id: string) => {
    if (unsubscribeChat !== null) {
      unsubscribeChat();
      unsubscribeChat = null;
    }

    didExist = false;

    const chatRef = doc(firestore, 'chats', id);
    unsubscribeChat = onSnapshot(chatRef, (docSnap) => {
      if (docSnap.exists()) {
        logger.info(`Chat ${id} doc found.`);
        didExist = true;
        // const data = docSnap.data() as ChatDocument;
        // if (!chat.value?.updatedAt || data.updatedAt.toDate() > new Date(chat.value?.updatedAt)) {
        //   fetch(docSnap.id);
        // }
      } else if (didExist) {
        logger.info(`Chat ${id} doc deleted.`);
        isChatNotFound.value = true;
        chat.value = null;
      } else {
        logger.warn(`Chat ${id} doc not found. Requesting.`);
        gambits.chatService.createFirestoreDoc(id);
      }
    });
  };

  const subscribeToMessages = (chatId: string, lastMessageAt?: string) => {
    if (unsubscribeMessages !== null) {
      unsubscribeMessages();
      unsubscribeMessages = null;
    }

    const messagesRef = collection(firestore, 'chats', chatId, 'messages');

    const baseQuery = lastMessageAt
      ? query(messagesRef, where('createdAt', '>', new Date(lastMessageAt)), orderBy('createdAt', 'asc'))
      : query(messagesRef, orderBy('createdAt', 'asc'));

    unsubscribeMessages = onSnapshot(baseQuery, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          logger.info(`Message ${change.doc.id} received.`);

          const { username, ...data } = change.doc.data() as MessageDocument;

          const isCurrentIdPresent = messages.value.some((m) => m.id === data.id);

          if (isCurrentIdPresent) {
            return;
          }

          messages.value.push({
            ...data,
            user: username ? { username } : undefined,
            createdAt: data.createdAt.toDate().toISOString(),
            updatedAt: data.createdAt.toDate().toISOString(),
          });

          return;
        }

        const messageIndex = messages.value.findIndex((m) => m.id === change.doc.id);
        if (change.type === 'removed' && messageIndex > -1) {
          logger.info(`Message ${change.doc.id} removed.`);

          messages.value.splice(messageIndex, 1);

          return;
        }
      });
    });
  };

  const fetchChat = async (chatId: string): Promise<ChatDto | null> => {
    isChatLoading.value = true;
    isChatError.value = false;
    isChatNotFound.value = false;

    const isRefresh = chat.value?.id === chatId;

    if (!isRefresh) {
      chat.value = null;
    }

    const chatResult = await gambits.chatService.getChat(chatId);
    if (chatId !== currentChatId) return null;

    isChatLoading.value = false;

    if (chatResult.data) {
      chat.value = chatResult.data;

      if (!isRefresh) {
        subscribeToChat(chat.value.id);
      }

      return chatResult.data;
    } else if (chatResult?.error?.code === 404) {
      isChatNotFound.value = true;
      chat.value = null;
    } else {
      isChatError.value = true;
    }

    return null;
  };

  const fetchMessages = async (chatId: string): Promise<MessageDto[] | null> => {
    areMessagesLoading.value = true;
    areMessagesError.value = false;
    areMessagesNotFound.value = false;

    const response = await gambits.chatService.getMessages(chatId);
    if (chatId !== currentChatId) return null;

    areMessagesLoading.value = false;

    if (response.data) {
      const chatMessages = response.data;
      messages.value = chatMessages;
      // const lastMessageAt = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1].createdAt : undefined;
      // subscribeToMessages(chatId, lastMessageAt);
      subscribeToMessages(chatId);

      return response.data;
    } else if (response?.error?.code === 404) {
      areMessagesNotFound.value = true;
      messages.value = [];
    } else {
      areMessagesError.value = true;
    }

    return null;
  };

  const fetch = async (chatId: string) => {
    areMessagesInitialized.value = false;

    currentChatId = chatId;

    await fetchChat(chatId);
    await fetchMessages(chatId);

    if (!areMessagesError.value) {
      areMessagesInitialized.value = true;
    }
  };

  const replace = async (newChat: ChatDto) => {
    if (!newChat) return;

    areMessagesInitialized.value = false;

    currentChatId = newChat.id;
    chat.value = newChat;

    subscribeToChat(newChat.id);

    await fetchMessages(newChat.id);

    if (!areMessagesError.value) {
      areMessagesInitialized.value = true;
    }
  };

  const sendMessage = async (body: string) => {
    if (!currentChatId) return;

    isMessageError.value = false;
    isMessageSubmitting.value = true;

    const response = await gambits.chatService.postMessage(currentChatId, body);
    isMessageSubmitting.value = false;

    if (response?.data) {
      const currentMessages = messages.value;
      const isCurrentIdPresent = currentMessages.some((m) => m.id === response.data?.id);

      !isCurrentIdPresent && messages.value.push(response.data);

      return response.data;
    } else if (response?.error) {
      isMessageError.value = true;
    }

    return null;
  };

  const deleteMessage = async (chatId: string, messageId: string) => {
    isMessageDeleteError.value = false;
    isMessageDeleting.value = true;

    const response = await gambits.chatService.deleteMessage(chatId, messageId);
    isMessageDeleting.value = false;

    const messageIndex = messages.value.findIndex((m) => m.id === messageId);
    if (!response?.error && messageIndex > -1) {
      messages.value.splice(messageIndex, 1);
    } else if (response?.error) {
      isMessageError.value = true;
    }
  };

  return {
    chat,
    messages,
    isChatLoading,
    isChatError,
    isChatNotFound,
    areMessagesInitialized,
    areMessagesLoading,
    areMessagesError,
    areMessagesNotFound,
    isMessageSubmitting,
    isMessageError,
    clear,
    fetchChat,
    fetchMessages,
    fetch,
    replace,
    sendMessage,
    deleteMessage,
  };
});
